// Handles animation and state for a single <details> element
class AccordionItem {
  constructor(element, selectors) {
    this.element = element;
    this.header = element.querySelector(selectors.header);

    this.animation = null;
    this.isAnimating = false;
    this.isClosing = false;
    this.isExpanding = false;

    this.header.addEventListener("click", event => this.onClick(event));
  }

  // Handles header click: toggles open/close state
  onClick(event) {
    event.preventDefault();
    if (this.isAnimating) return;

    const isOpen = this.element.hasAttribute("open");
    isOpen ? this.close() : this.open();
  }

  // Starts closing animation and updates state
  close() {
    this.cancelAnimation();
    const startHeight = this.element.offsetHeight;
    this.setTransitionState(startHeight);
    this.isClosing = true;

    this.animateTransition(
      startHeight,
      this.header.offsetHeight,
      () => {
        this.element.removeAttribute("open");
        this.clearTransitionState();
        this.isClosing = false;
        this.isAnimating = false;
      },
      () => {
        this.isClosing = false;
        this.isAnimating = false;
      }
    );
  }

  // Starts opening animation and updates state
  open() {
    this.cancelAnimation();
    const startHeight = this.element.offsetHeight;
    this.setTransitionState(startHeight);
    this.element.setAttribute("open", true);
    this.isExpanding = true;

    requestAnimationFrame(() => {
      this.animateTransition(
        startHeight,
        this.element.scrollHeight,
        () => {
          this.clearTransitionState();
          this.isExpanding = false;
          this.isAnimating = false;
        },
        () => {
          this.isExpanding = false;
          this.isAnimating = false;
        }
      );
    });
  }

  // Animates the height change and calls finish/cancel callbacks
  animateTransition(from, to, onFinish, onCancel) {
    this.isAnimating = true;

    const anim = this.element.animate(
      [
        { height: `${from}px` },
        { height: `${to}px` }
      ],
      {
        duration: 500,
        easing: from < to ? "ease-in" : "ease-out"
      }
    );

    anim.addEventListener("finish", () => {
      this.animation = null;
      onFinish?.();
    });

    onCancel && anim.addEventListener("cancel", onCancel);

    this.animation = anim;
  }

  // Locks the height and overflow for animation
  setTransitionState(height) {
    this.element.style.overflow = "hidden";
    this.element.style.height = `${height}px`;
  }

  // Removes inline animation styles
  clearTransitionState() {
    this.element.style.removeProperty("overflow");
    this.element.style.removeProperty("height");
  }

  // Cancels any active animation
  cancelAnimation() {
    if (this.animation) {
      this.animation.cancel();
      this.animation = null;
    }
  }
}

// Manages multiple accordion elements on the page
class AccordionController {
  constructor({ selectors }) {
    this.selectors = selectors;
    this.accordions = [];
  }

  // Scans for new matching elements and binds them
  bindNew() {
    this.getUnboundElements().forEach(element => {
      const accordeon = new AccordionItem(element, this.selectors);
      this.accordions.push(accordeon);
      element.__boundAccordion = true;
    });
  }

  // Returns <details> elements that haven't been initialized yet
  getUnboundElements() {
    const allElements = document.querySelectorAll(this.selectors.details);
    return Array.from(allElements).filter(el => !el.__boundAccordion);
  }
}

export default AccordionController;
